// ---------------------------------------------------------------------------
// Homefront v3 — auto-generate: human-plausible homefronts for the maker
// ---------------------------------------------------------------------------
//
// The maker's Auto-generate button. Resources are out of the prototype, so a
// Grid is its structure roster: this places the required buildings (a flat 7 —
// HOMEFRONT_MIN/MAX_STRUCTURES agree) the way a thoughtful player would —
// shapes drawn under the per-shape cap (the maker's own hex shapes, centred
// on a hex the way a tap would centre them), one building anchored per plot,
// plots spread away from each other (one blast should never menace two), and
// anchors kept ≥ AUTOGEN_MIN_SPACING hexes apart so a margin-1 crater (the
// aim's ring of 6) can never clip two buildings' centres.
//
// Every call returns a fresh homefront that passes validateMaker(). Pass a
// seeded rng for deterministic output (tests); defaults to Math.random.

import { rules } from './config.js'
import {
    PLOT_COLS,
    PLOT_ROWS,
    plotSubIndices,
    makeHomefront,
    placeStructure,
} from './subplotGrid.js'
import { hexDistance } from './hexGrid.js'
import { STRUCTURE_SHAPES, shapeFootprint, canPlaceFootprint } from './homefrontMaker.js'

export const AUTOGEN_MIN_SPACING = 4

function shuffle(arr, rng) {
    const out = [...arr]
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1))
        ;[out[i], out[j]] = [out[j], out[i]]
    }
    return out
}

export function generateHomefrontV3(rng = Math.random) {
    const randInt = (n) => Math.floor(rng() * n)

    const hf = makeHomefront(() => null)

    const count = rules().homefrontMinStructures
        + randInt(rules().homefrontMaxStructures - rules().homefrontMinStructures + 1)

    const shapeUse = {}
    const availableShapes = () =>
        STRUCTURE_SHAPES.map(sh => sh.key).filter(k => (shapeUse[k] ?? 0) < rules().homefrontMaxPerShape)

    const usedPlots = new Set()
    const anchors = []          // placed centre cells, for spacing checks
    let placedCount = 0

    const plotKey = (col, row) => `${col},${row}`
    const place = (col, row, shape, footprint) => {
        placedCount++
        placeStructure(hf, `A-${placedCount}`, footprint)
        shapeUse[shape] = (shapeUse[shape] ?? 0) + 1
        usedPlots.add(plotKey(col, row))
        anchors.push(footprint[Math.floor(footprint.length / 2)])
    }
    const spaced = (s) => anchors.every(a => hexDistance(a, s) >= AUTOGEN_MIN_SPACING)

    // All plots, preferring ones not cardinally adjacent to a used plot so
    // buildings spread out (one blast shouldn't menace two at once).
    const allPlots = []
    for (let col = 0; col < PLOT_COLS; col++) {
        for (let row = 0; row < PLOT_ROWS; row++) allPlots.push({ col, row })
    }
    const freePlots = () => {
        const free = allPlots.filter(p => !usedPlots.has(plotKey(p.col, p.row)))
        const isolated = free.filter(p =>
            ![[0, 1], [0, -1], [1, 0], [-1, 0]].some(([dc, dr]) =>
                usedPlots.has(plotKey(p.col + dc, p.row + dr))))
        const crowded = free.filter(p => !isolated.includes(p))
        return [...shuffle(isolated, rng), ...shuffle(crowded, rng)]
    }

    // A footprint for `shape` centred somewhere in plot (col, row): the
    // building may spill over the plot's seams (the field is continuous) but
    // never onto another building. `strict` also demands the spacing.
    const fit = (col, row, shape, strict) => {
        for (const s of shuffle(plotSubIndices(col, row), rng)) {
            const footprint = shapeFootprint(shape, s)
            if (!footprint || !canPlaceFootprint(hf, footprint)) continue
            if (strict && !spaced(s)) continue
            return footprint
        }
        return null
    }

    while (placedCount < count) {
        let placed = null
        // Keep buildings off each other's doorsteps while plots remain.
        const strict = freePlots().length > count - placedCount
        for (const { col, row } of freePlots()) {
            for (const shape of shuffle(availableShapes(), rng)) {
                const footprint = fit(col, row, shape, strict)
                if (footprint) { placed = { col, row, shape, footprint }; break }
            }
            if (placed) break
        }
        // Safety net: any free plot, any shape, spacing waived — the board
        // always reaches `count` (validateMaker minimum). A single always
        // fits on an unused plot.
        if (!placed) {
            for (const { col, row } of freePlots()) {
                for (const shape of shuffle(availableShapes(), rng)) {
                    const footprint = fit(col, row, shape, false)
                    if (footprint) { placed = { col, row, shape, footprint }; break }
                }
                if (placed) break
            }
        }
        place(placed.col, placed.row, placed.shape, placed.footprint)
    }

    return hf
}
